/**
 * Passaggi della vista di esecuzione: quali misure si scrivono, dalle celle ai
 * risultati, dai risultati salvati alle celle, e chi compare nella tabella.
 * Le celle stanno per giocatore e misura; la misura ha per chiave l'id della
 * misura del test in un'esecuzione nuova e l'id della copia in correzione,
 * come vuole il server.
 *
 * Il punteggio mostrato mentre si scrive viene da `calcolaPunteggio`, lo
 * stesso del server; quello salvato lo ricalcola sempre il server.
 */

import {
  calcolaPunteggio,
  risultatoTestSchema,
  type MetodoTest,
  type ParametriTest,
  type RuoloGiocatore,
} from '@calcio/shared';
import { CAMPI_RISULTATO, risultatoDaForm, type TestDettaglio } from './test-misura.js';

export interface GiocatoreIdoneo {
  id: string;
  nome: string;
  cognome: string;
  numeroMaglia: number | null;
  ruolo: RuoloGiocatore;
}

/** Il blocco di seduta in cui il test si è svolto. */
export interface BloccoDellEsecuzione {
  id: string;
  allenamentoId: string;
}

/**
 * La rosa in corso della squadra: un test lo svolge chiunque. Da un blocco di
 * seduta, i presenti dei suoi gruppi, con il giorno della seduta.
 */
export interface Idonei {
  giocatori: GiocatoreIdoneo[];
  blocco?: BloccoDellEsecuzione & { data: string; esecuzioneId: string | null };
}

export interface EsecuzioneInElenco {
  id: string;
  data: string;
  autore: string | null;
  giocatori: number;
  blocco: BloccoDellEsecuzione | null;
}

export interface RisultatoSalvato {
  risultato: Record<string, number>;
  punteggio: number;
  derivato: number | null;
}

export interface RigaSalvata {
  giocatoreId: string;
  nome: string;
  cognome: string;
  /** Per id di misura copiata; manca la misura che il giocatore non ha fatto. */
  risultati: Record<string, RisultatoSalvato>;
}

/** La copia di una misura del test nell'esecuzione. */
export interface MisuraEsecuzione {
  id: string;
  misuraId: string | null;
  ordine: number;
  nome: string;
  unita: string | null;
  metodo: MetodoTest;
  parametri: ParametriTest;
  attributi: { id: string; nome: string }[];
}

export interface EsecuzioneDettaglio {
  id: string;
  test: { id: string; nome: string };
  squadraStagioneId: string;
  data: string;
  autore: string | null;
  blocco: BloccoDellEsecuzione | null;
  misure: MisuraEsecuzione[];
  righe: RigaSalvata[];
}

/** Una misura come la usa la vista: la chiave è l'id da mandare al server. */
export interface MisuraDellaVista {
  chiave: string;
  nome: string;
  unita: string | null;
  parametri: ParametriTest;
  attributi: { id: string; nome: string }[];
}

/** Esecuzione nuova: le misure del test con almeno un attributo attivo, come il server. */
export function misureDaEseguire(test: TestDettaglio): MisuraDellaVista[] {
  return test.misure.flatMap((m) => {
    const attivi = m.attributi.filter((a) => a.attivo).map(({ id, nome }) => ({ id, nome }));
    return attivi.length === 0
      ? []
      : [{ chiave: m.id, nome: m.nome, unita: m.unita, parametri: m.parametri, attributi: attivi }];
  });
}

/** Correzione: le misure copiate nell'esecuzione, non quelle di oggi. */
export function misureDellEsecuzione(e: EsecuzioneDettaglio): MisuraDellaVista[] {
  return e.misure.map((m) => ({
    chiave: m.id,
    nome: m.nome,
    unita: m.unita,
    parametri: m.parametri,
    attributi: m.attributi,
  }));
}

/** Le celle di un giocatore, testi come l'utente li scrive. */
export type ValoriRiga = Record<string, string>;

export type EsitoRiga =
  | { stato: 'vuota' }
  | { stato: 'errore'; errori: Record<string, string> }
  | {
      stato: 'piena';
      risultato: Record<string, number>;
      punteggio: number;
      derivato: number | null;
    };

const vuoto = (t: string | undefined) => (t ?? '').trim() === '';

export function valutaRiga(parametri: ParametriTest, valori: ValoriRiga): EsitoRiga {
  const campi = CAMPI_RISULTATO[parametri.metodo];
  if (campi.every((c) => vuoto(valori[c.chiave]))) return { stato: 'vuota' };
  const esito = risultatoTestSchema(parametri).safeParse(risultatoDaForm(parametri.metodo, valori));
  if (!esito.success) {
    const errori: Record<string, string> = {};
    for (const { path, message } of esito.error.issues) errori[String(path[0] ?? '')] ??= message;
    return { stato: 'errore', errori };
  }
  const risultato = esito.data as Record<string, number>;
  const { punteggio, derivato } = calcolaPunteggio(parametri, risultato);
  return { stato: 'piena', risultato, punteggio, derivato: derivato ?? null };
}

/** Le celle di ogni giocatore, per chiave di misura. */
export type ValoriEsecuzione = Record<string, Record<string, ValoriRiga>>;

/**
 * Le righe da mandare, dei soli giocatori scelti: le misure vuote si
 * scartano, e così le righe senza nessuna misura piena; ne serve almeno una,
 * e una misura sbagliata ferma tutto.
 */
export function righeDaSalvare(
  misure: readonly MisuraDellaVista[],
  scelti: readonly string[],
  valori: ValoriEsecuzione,
):
  | { righe: { giocatoreId: string; risultati: Record<string, Record<string, number>> }[] }
  | { errore: string; errori: Record<string, Record<string, Record<string, string>>> } {
  const righe = [];
  const errori: Record<string, Record<string, Record<string, string>>> = {};
  for (const giocatoreId of scelti) {
    const risultati: Record<string, Record<string, number>> = {};
    for (const m of misure) {
      const esito = valutaRiga(m.parametri, valori[giocatoreId]?.[m.chiave] ?? {});
      if (esito.stato === 'errore') {
        errori[giocatoreId] = { ...errori[giocatoreId], [m.chiave]: esito.errori };
      }
      if (esito.stato === 'piena') risultati[m.chiave] = esito.risultato;
    }
    if (Object.keys(risultati).length > 0) righe.push({ giocatoreId, risultati });
  }
  if (Object.keys(errori).length > 0) return { errore: 'Correggi i risultati segnati.', errori };
  if (righe.length === 0) return { errore: 'Inserisci almeno un risultato.', errori };
  return { righe };
}

/** Quante righe hanno almeno una misura piena. */
export function righeCompilate(
  misure: readonly MisuraDellaVista[],
  giocatori: readonly string[],
  valori: ValoriEsecuzione,
): number {
  return giocatori.filter((g) =>
    misure.some((m) => valutaRiga(m.parametri, valori[g]?.[m.chiave] ?? {}).stato === 'piena'),
  ).length;
}

const testo = (n: number) => String(n).replace('.', ',');

export function valoriDaRisultato(
  metodo: MetodoTest,
  risultato: Record<string, number>,
): ValoriRiga {
  const valori: ValoriRiga = {};
  for (const c of CAMPI_RISULTATO[metodo]) {
    const v = risultato[c.chiave];
    if (typeof v === 'number') valori[c.chiave] = testo(v);
  }
  return valori;
}

/** Le celle di un'esecuzione salvata, per giocatore e misura copiata. */
export function valoriDaEsecuzione(e: EsecuzioneDettaglio): ValoriEsecuzione {
  const metodo = new Map(e.misure.map((m) => [m.id, m.metodo]));
  return Object.fromEntries(
    e.righe.map((r) => [
      r.giocatoreId,
      Object.fromEntries(
        Object.entries(r.risultati).map(([chiave, v]) => [
          chiave,
          valoriDaRisultato(metodo.get(chiave)!, v.risultato),
        ]),
      ),
    ]),
  );
}

export interface GiocatoreDellaVista {
  id: string;
  nome: string;
  cognome: string;
  numeroMaglia: number | null;
  ruolo: RuoloGiocatore | null;
  /** Salvato nell'esecuzione ma non più in rosa: resta, non si aggiunge. */
  fuoriRosa: boolean;
}

/**
 * La rosa di oggi e, in correzione, chi è già nell'esecuzione anche se nel
 * frattempo ha chiuso la tessera: il server lo tiene, la vista lo mostra.
 */
export function giocatoriDellaVista(
  idonei: readonly GiocatoreIdoneo[],
  salvate: readonly Pick<RigaSalvata, 'giocatoreId' | 'nome' | 'cognome'>[],
): GiocatoreDellaVista[] {
  const presenti = new Set(idonei.map((g) => g.id));
  const tutti: GiocatoreDellaVista[] = [
    ...idonei.map((g) => ({ ...g, fuoriRosa: false })),
    ...salvate
      .filter((r) => !presenti.has(r.giocatoreId))
      .map((r) => ({
        id: r.giocatoreId,
        nome: r.nome,
        cognome: r.cognome,
        numeroMaglia: null,
        ruolo: null,
        fuoriRosa: true,
      })),
  ];
  return tutti.sort(
    (a, b) => a.cognome.localeCompare(b.cognome, 'it') || a.nome.localeCompare(b.nome, 'it'),
  );
}

const n = (v: number) => v.toLocaleString('it-IT');

/** Il risultato grezzo di un giocatore in una riga: «4,5 s», «livello 5, navetta 3 (700 m)». */
export function risultatoAParole(
  risultato: Record<string, number>,
  unita: string | null,
  derivato: number | null,
): string {
  const { valore, livello, navetta, tempo, errori } = risultato;
  if (livello !== undefined && navetta !== undefined) {
    const base = `livello ${n(livello)}, navetta ${n(navetta)}`;
    return derivato === null ? base : `${base} (${n(derivato)} m)`;
  }
  if (tempo !== undefined && errori !== undefined) {
    const base = `${n(tempo)} s, ${n(errori)} ${errori === 1 ? 'errore' : 'errori'}`;
    return derivato === null ? base : `${base} (${n(derivato)} s netti)`;
  }
  if (valore === undefined) return '—';
  return unita ? `${n(valore)} ${unita}` : n(valore);
}

/**
 * Da dove viene una misurazione: «Sprint 30 m · 4,5 s», e con più misure
 * anche la misura, «Sprint 100 m · Parziale 10 m · 1,85 s».
 */
export function provenienzaAParole(da: {
  test: string;
  misura: string;
  risultato: Record<string, number>;
  unita: string | null;
  derivato: number | null;
}): string {
  const origine = da.misura === da.test ? da.test : `${da.test} · ${da.misura}`;
  return `${origine} · ${risultatoAParole(da.risultato, da.unita, da.derivato)}`;
}
